import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, CheckCircle2, Loader2, Sparkles, XCircle } from "lucide-react";
import { toast } from "sonner";

import { useApiHeaders } from "@/hooks/use-api-headers";
import { useTranslation } from "@/i18n/use-translation";
import { formatNumber } from "@/lib/format";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { apiError, showApiErrorAs } from "@/lib/api-error";

const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:8000";

interface ProcessingStatus {
  total: number;
  processed: number;
  pending: number;
  food: number;
  non_food: number;
  status: string;
  recent: { description: string; clean_name: string; is_food: boolean }[];
  processing_error: string | null;
  failed_batches: number;
}

export function PreprocessingProgress({ listId, listName }: { listId: string; listName?: string }) {
  const { locale, t } = useTranslation();
  const headers = useApiHeaders();
  const queryClient = useQueryClient();

  const cancelMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${listId}/preprocess/cancel`, {
        method: "POST",
        headers,
      });
      if (!res.ok) {
        throw await apiError(t, res);
      }
    },
    onSuccess: () => {
      toast.success(t("settings.processingCancelled"));
      queryClient.invalidateQueries({ queryKey: ["preprocessing-status", listId] });
    },
    onError: (err: Error) => showApiErrorAs(t("settings.cancelProcessingFailed"), err),
  });

  const { data: status } = useQuery<ProcessingStatus>({
    queryKey: ["preprocessing-status", listId],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/api/v1/inventory-lists/${listId}/preprocess/status`, {
        headers,
      });
      if (!res.ok) throw new Error("Failed to fetch status");
      return (await res.json()).data;
    },
    refetchInterval: (query) => {
      const s = query.state.data?.status;
      return s === "processing" ? 2000 : false;
    },
  });

  if (!status) return null;

  const isProcessing = status.status === "processing";
  const isComplete = status.status === "complete";
  const isFailed = status.status === "failed";
  const isCancelled = status.status === "cancelled";

  const pct = status.total > 0 ? (status.processed / status.total) * 100 : 0;

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">
            {listName ?? t("settings.inventoryCatalogue")}
          </h2>
          <p className="text-xs text-muted-foreground">
            {t("settings.itemsCount", { count: formatNumber(locale, status.total) })}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isProcessing && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs text-gray-500 hover:text-red-600"
              onClick={() => cancelMutation.mutate()}
              disabled={cancelMutation.isPending}
            >
              {cancelMutation.isPending ? (
                <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              ) : (
                <XCircle className="mr-1 h-3 w-3" />
              )}
              {t("common.cancel")}
            </Button>
          )}
          {isComplete ? (
            <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
              <CheckCircle2 className="mr-1 h-3 w-3" />
              {t("settings.complete")}
            </Badge>
          ) : isFailed ? (
            <Badge className="bg-red-50 text-red-700 border-red-200">
              <AlertTriangle className="mr-1 h-3 w-3" />
              {t("settings.failed")}
            </Badge>
          ) : isCancelled ? (
            <Badge className="bg-amber-50 text-amber-700 border-amber-200">
              <XCircle className="mr-1 h-3 w-3" />
              {t("settings.cancelled")}
            </Badge>
          ) : isProcessing ? (
            <Badge className="bg-blue-50 text-blue-700 border-blue-200">
              <Loader2 className="mr-1 h-3 w-3 animate-spin" />
              {t("settings.processing")}
            </Badge>
          ) : (
            <Badge className="bg-gray-50 text-gray-600 border-gray-200">{status.status}</Badge>
          )}
        </div>
      </div>

      {/* Progress bar */}
      <div className="space-y-1.5">
        <div className="flex items-center justify-between text-xs">
          <span className="flex items-center gap-1.5 text-gray-500">
            <Sparkles className="h-3 w-3" />
            {t("settings.processingCatalogue")}
          </span>
          <span className="font-medium text-gray-700">
            {formatNumber(locale, status.processed)} / {formatNumber(locale, status.total)}
          </span>
        </div>
        <div className="h-2 w-full rounded-full bg-gray-100">
          <div
            className={`h-2 rounded-full transition-all duration-500 ${isComplete ? "bg-emerald-500" : "bg-blue-500"}`}
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
        {isProcessing && (
          <p className="text-[11px] text-gray-400">
            {t("settings.percentComplete", { percent: Math.round(pct) })}
          </p>
        )}
      </div>

      {/* Error */}
      {isFailed && status.processing_error && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3">
          <p className="text-xs font-medium text-red-700">{t("settings.processingError")}</p>
          <p className="mt-1 text-xs text-red-600">{status.processing_error}</p>
        </div>
      )}

      {/* Classification stats */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-lg border border-gray-100 p-3">
          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
            {t("settings.foodItems")}
          </p>
          <p className="mt-1 text-lg font-semibold text-gray-900">
            {formatNumber(locale, status.food)}
          </p>
        </div>
        <div className="rounded-lg border border-gray-100 p-3">
          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
            {t("settings.nonFood")}
          </p>
          <p className="mt-1 text-lg font-semibold text-gray-900">
            {formatNumber(locale, status.non_food)}
          </p>
        </div>
        <div className="rounded-lg border border-gray-100 p-3">
          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">
            {t("settings.processed")}
          </p>
          <p className="mt-1 text-lg font-semibold text-gray-900">
            {formatNumber(locale, status.processed)}
          </p>
        </div>
      </div>

      {/* Recent items */}
      {isProcessing && status.recent.length > 0 && (
        <div>
          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide mb-2">
            {t("settings.recentItemsProcessed")}
          </p>
          <div className="space-y-1">
            {status.recent.map((item, i) => (
              <div key={i} className="flex items-center gap-3 rounded px-2 py-1.5 text-xs">
                <span className="text-gray-400 truncate flex-1 min-w-0">{item.description}</span>
                <span className="text-gray-900 font-medium truncate flex-1 min-w-0">
                  {item.clean_name}
                </span>
                <Badge
                  className={`shrink-0 border-0 text-[10px] ${
                    item.is_food ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-600"
                  }`}
                >
                  {item.is_food ? t("settings.food") : t("settings.nonFood")}
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
